import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@/components/ui/button'
import Link from '@/components/ui/link'
import BravicLogoAnimated from '../ui/bravic-logo-animated'
import LanguageSwitcher from '../ui/language-switcher'
import { getPath, onPathChange, navigate } from '@/lib/navigation'

const navKeys = [
  { key: 'header.nav.services', href: '/roadmap' },
  { key: 'header.nav.about', href: '/about' },
  { key: 'header.nav.cases', href: '/cases', scroll: true },
  { key: 'header.nav.manifesto', href: '/manifesto' },
]

function scrollToCases() {
  const tryScroll = (attempts = 0) => {
    const el = document.getElementById('cases')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    } else if (attempts < 20) {
      setTimeout(() => tryScroll(attempts + 1), 50)
    }
  }
  tryScroll()
}

export default function Header() {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [currentPath, setCurrentPath] = useState(getPath())
  const [casesInView, setCasesInView] = useState(false)

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  useEffect(() => {
    return onPathChange(() => setCurrentPath(getPath()))
  }, [])

  useEffect(() => {
    if (currentPath !== '/') {
      setCasesInView(false)
      return
    }
    const observe = (attempts = 0): (() => void) | undefined => {
      const el = document.getElementById('cases')
      if (!el) {
        if (attempts < 20) {
          const id = setTimeout(() => observe(attempts + 1), 100)
          return () => clearTimeout(id)
        }
        return
      }
      const observer = new IntersectionObserver(([entry]) => setCasesInView(entry.isIntersecting), {
        rootMargin: '-40% 0px -40% 0px',
      })
      observer.observe(el)
      return () => observer.disconnect()
    }
    const cleanup = observe()
    return cleanup
  }, [currentPath])

  const isActive = (link: (typeof navKeys)[number]) => {
    if (link.scroll) return currentPath === '/' && casesInView
    return currentPath === link.href
  }

  const handleCasesClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setMenuOpen(false)
    if (currentPath === '/') {
      scrollToCases()
    } else {
      sessionStorage.setItem('scrollToCases', '1')
      navigate('/')
    }
  }

  useEffect(() => {
    if (currentPath === '/' && sessionStorage.getItem('scrollToCases')) {
      sessionStorage.removeItem('scrollToCases')
      scrollToCases()
    }
  }, [currentPath])

  const handleNavClick = () => {
    setMenuOpen(false)
  }

  return (
    <nav className="fixed top-0 w-full z-50 bg-surface/60 backdrop-blur-xl shadow-2xl shadow-black/20 bg-linear-to-b from-surface-container-lowest to-transparent">
      <div className="flex justify-between items-center px-5 sm:px-8 md:px-10 lg:px-12 py-4 md:py-6 max-w-[1440px] mx-auto">
        <Link href="/">
          <BravicLogoAnimated className="text-2xl" />
        </Link>

        <div className="hidden md:flex gap-10 font-display tracking-tighter uppercase text-label-md font-medium items-center">
          {navKeys.map((link) => (
            <Link
              key={link.key}
              href={link.href}
              onClick={link.scroll ? handleCasesClick : undefined}
              className={`transition-colors duration-300 hover:text-primary ${
                isActive(link) ? 'text-primary' : 'text-on-surface-variant'
              }`}
            >
              {t(link.key)}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3 md:gap-6">
          <Link
            href="/contact"
            className="hidden md:block"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <Button variant="gradient" size="sm">
              {t('header.contact')}
            </Button>
          </Link>
          <LanguageSwitcher />
          <button
            className="md:hidden p-2 text-on-surface"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <span className="material-symbols-outlined text-2xl">
              {menuOpen ? 'close' : 'menu'}
            </span>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-surface/95 backdrop-blur-xl border-t border-outline-variant/10 px-5 pb-6">
          <div className="flex flex-col gap-1 pt-4 font-display tracking-tighter uppercase text-label-md font-medium">
            {navKeys.map((link) => (
              <Link
                key={link.key}
                href={link.href}
                onClick={link.scroll ? handleCasesClick : handleNavClick}
                className={`transition-colors py-3 hover:text-primary ${
                  isActive(link) ? 'text-primary' : 'text-on-surface-variant'
                }`}
              >
                {t(link.key)}
              </Link>
            ))}
            <Link
              href="/contact"
              className="mt-4"
              onClick={() => {
                setMenuOpen(false)
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
            >
              <Button variant="gradient" size="sm" className="w-full">
                {t('header.contact')}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  )
}
